package com.example.event_management_system.ui.screens.admin

import cafe.adriel.voyager.core.model.ScreenModel
import cafe.adriel.voyager.core.model.screenModelScope
import com.example.event_management_system.data.models.EmailWorkspace
import com.example.event_management_system.data.models.Location
import com.example.event_management_system.data.models.Section
import com.example.event_management_system.data.models.User
import com.example.event_management_system.data.models.UserRole
import com.example.event_management_system.data.repository.IAuthRepository
import com.example.event_management_system.data.repository.IEmailRepository
import com.example.event_management_system.data.repository.IEmailWorkspaceRepository
import com.example.event_management_system.data.repository.ILocationRepository
import com.example.event_management_system.data.repository.ISectionRepository
import com.example.event_management_system.data.repository.IUserRepository
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

enum class AdminTab(val title: String) {
    EMAIL_WORKSPACES("Email Workspaces"),
    TEAM_MEMBERS("Team Members"),
    LOCATIONS("Locations"),
    REPORTS("Reports")
}

data class AdminDashboardState(
    val currentUser: User? = null,
    val isLoading: Boolean = true,
    val accessDenied: Boolean = false,
    val selectedTab: AdminTab = AdminTab.EMAIL_WORKSPACES,
    val workspaces: List<EmailWorkspace> = emptyList(),
    val isWorkspacesLoading: Boolean = false,
    val workspacesError: String? = null,
    val isSavingWorkspace: Boolean = false,
    val isTestSending: Boolean = false,
    val locations: List<Location> = emptyList(),
    val sectionsMap: Map<String, List<Section>> = emptyMap(),
    val isLocationsLoading: Boolean = false,
    val locationsError: String? = null,
    val isSavingLocation: Boolean = false,
    val editingEmailTemplate: Location? = null,
    val isSavingTemplate: Boolean = false,
    val message: String? = null,
    val error: String? = null,
    val users: List<User> = emptyList(),
    val isUsersLoading: Boolean = false,
    val usersError: String? = null,
    val isSavingUser: Boolean = false
)

class AdminDashboardScreenModel(
    private val authRepository: IAuthRepository,
    private val userRepository: IUserRepository,
    private val workspaceRepository: IEmailWorkspaceRepository,
    private val emailRepository: IEmailRepository,
    private val locationRepository: ILocationRepository,
    private val sectionRepository: ISectionRepository
) : ScreenModel {

    private val sectionJobs = mutableMapOf<String, Job>()

    private val _state = MutableStateFlow(AdminDashboardState())
    val state: StateFlow<AdminDashboardState> = _state.asStateFlow()

    init {
        loadUserAndGate()
    }

    private fun loadUserAndGate() {
        screenModelScope.launch {
            val userId = authRepository.getCurrentUserId()
            if (userId == null) {
                _state.update { it.copy(isLoading = false, accessDenied = true) }
                return@launch
            }
            val user = userRepository.getUser(userId)
            if (user == null || user.role != UserRole.OWNER) {
                _state.update { it.copy(isLoading = false, accessDenied = true, currentUser = user) }
                return@launch
            }
            _state.update { it.copy(currentUser = user, isLoading = false, accessDenied = false) }
            observeWorkspaces()
        }
    }

    private fun observeWorkspaces() {
        screenModelScope.launch {
            _state.update { it.copy(isWorkspacesLoading = true) }
            workspaceRepository.getWorkspacesFlow()
                .catch { e ->
                    _state.update {
                        it.copy(
                            isWorkspacesLoading = false,
                            workspacesError = e.message ?: "Failed to load workspaces."
                        )
                    }
                }
                .collect { list ->
                    _state.update {
                        it.copy(
                            workspaces = list,
                            isWorkspacesLoading = false,
                            workspacesError = null
                        )
                    }
                }
        }
    }

    fun selectTab(tab: AdminTab) {
        _state.update { it.copy(selectedTab = tab) }
        if (tab == AdminTab.LOCATIONS && _state.value.locations.isEmpty()) observeLocations()
        if (tab == AdminTab.TEAM_MEMBERS) {
            if (_state.value.users.isEmpty()) observeUsers()
            if (_state.value.locations.isEmpty()) observeLocations()
        }
    }

    private fun observeLocations() {
        screenModelScope.launch {
            _state.update { it.copy(isLocationsLoading = true) }
            locationRepository.getAllLocationsFlow()
                .catch { e ->
                    _state.update { it.copy(isLocationsLoading = false, locationsError = e.message ?: "Failed to load venues.") }
                }
                .collect { locations ->
                    _state.update { it.copy(locations = locations, isLocationsLoading = false) }
                    val currentIds = locations.map { it.id }.toSet()
                    sectionJobs.keys.filter { it !in currentIds }.forEach { id ->
                        sectionJobs.remove(id)?.cancel()
                    }
                    locations.forEach { location ->
                        if (location.id !in sectionJobs) {
                            sectionJobs[location.id] = launch {
                                sectionRepository.getSectionsByLocationFlow(location.id).collect { sections ->
                                    _state.update { s -> s.copy(sectionsMap = s.sectionsMap + (location.id to sections)) }
                                }
                            }
                        }
                    }
                }
        }
    }

    private fun observeUsers() {
        screenModelScope.launch {
            _state.update { it.copy(isUsersLoading = true) }
            userRepository.getAllUsersFlow()
                .catch { e ->
                    _state.update { it.copy(isUsersLoading = false, usersError = e.message ?: "Failed to load team members.") }
                }
                .collect { list ->
                    _state.update { it.copy(users = list, isUsersLoading = false, usersError = null) }
                }
        }
    }

    fun saveTeamMember(user: User) {
        screenModelScope.launch {
            _state.update { it.copy(isSavingUser = true, error = null) }
            if (user.id.isEmpty()) {
                emailRepository.createTeamMember(
                    email = user.email,
                    displayName = user.displayName,
                    phone = user.phone,
                    role = user.role.name,
                    locationIds = user.locationIds
                ).fold(
                    onSuccess = {
                        _state.update {
                            it.copy(
                                isSavingUser = false,
                                message = "\"${user.displayName}\" invited. They'll receive an email to set their password."
                            )
                        }
                    },
                    onFailure = { e ->
                        _state.update { it.copy(isSavingUser = false, error = e.message ?: "Failed to create team member.") }
                    }
                )
            } else {
                try {
                    userRepository.updateUser(user.id, user)
                    _state.update { it.copy(isSavingUser = false, message = "Saved \"${user.displayName}\".") }
                } catch (e: Throwable) {
                    _state.update { it.copy(isSavingUser = false, error = e.message ?: "Failed to update team member.") }
                }
            }
        }
    }

    fun setUserActive(userId: String, active: Boolean) {
        screenModelScope.launch {
            try {
                val user = _state.value.users.find { it.id == userId } ?: return@launch
                userRepository.updateUser(userId, user.copy(isActive = active))
                val word = if (active) "reactivated" else "deactivated"
                _state.update { it.copy(message = "\"${user.displayName}\" $word.") }
            } catch (e: Throwable) {
                _state.update { it.copy(error = e.message ?: "Failed to update member.") }
            }
        }
    }

    fun deleteTeamMember(userId: String) {
        screenModelScope.launch {
            val user = _state.value.users.find { it.id == userId } ?: return@launch
            emailRepository.deleteTeamMember(userId).fold(
                onSuccess = {
                    _state.update { it.copy(message = "\"${user.displayName}\" has been deleted.") }
                },
                onFailure = { e ->
                    _state.update { it.copy(error = e.message ?: "Failed to delete team member.") }
                }
            )
        }
    }

    fun saveLocation(location: Location) {
        screenModelScope.launch {
            _state.update { it.copy(isSavingLocation = true, error = null) }
            try {
                if (location.id.isEmpty()) {
                    val ownerId = authRepository.getCurrentUserId() ?: ""
                    locationRepository.createLocation(location.copy(ownerId = ownerId))
                } else {
                    locationRepository.updateLocation(location.id, location)
                }
                _state.update { it.copy(isSavingLocation = false, message = "Saved \"${location.name}\".") }
            } catch (e: Throwable) {
                _state.update { it.copy(isSavingLocation = false, error = e.message ?: "Failed to save venue.") }
            }
        }
    }

    fun deleteLocation(locationId: String) {
        screenModelScope.launch {
            try {
                locationRepository.deleteLocation(locationId)
                sectionJobs.remove(locationId)?.cancel()
                _state.update { s -> s.copy(
                    sectionsMap = s.sectionsMap - locationId,
                    message = "Venue deleted."
                ) }
            } catch (e: Throwable) {
                _state.update { it.copy(error = e.message ?: "Failed to delete venue.") }
            }
        }
    }

    fun saveSection(section: Section) {
        screenModelScope.launch {
            try {
                if (section.id.isEmpty()) {
                    sectionRepository.createSection(section)
                } else {
                    sectionRepository.updateSection(section.id, section)
                }
                _state.update { it.copy(message = "Saved \"${section.name}\".") }
            } catch (e: Throwable) {
                _state.update { it.copy(error = e.message ?: "Failed to save area.") }
            }
        }
    }

    fun deleteSection(sectionId: String) {
        screenModelScope.launch {
            try {
                sectionRepository.deleteSection(sectionId)
                _state.update { it.copy(message = "Area deleted.") }
            } catch (e: Throwable) {
                _state.update { it.copy(error = e.message ?: "Failed to delete area.") }
            }
        }
    }

    fun saveWorkspace(workspace: EmailWorkspace) {
        screenModelScope.launch {
            _state.update { it.copy(isSavingWorkspace = true, error = null) }
            try {
                workspaceRepository.upsertWorkspace(workspace)
                _state.update {
                    it.copy(
                        isSavingWorkspace = false,
                        message = "Saved \"${workspace.domain}\"."
                    )
                }
            } catch (e: Throwable) {
                _state.update {
                    it.copy(
                        isSavingWorkspace = false,
                        error = e.message ?: "Failed to save workspace."
                    )
                }
            }
        }
    }

    fun deleteWorkspace(domain: String) {
        screenModelScope.launch {
            try {
                workspaceRepository.deleteWorkspace(domain)
                _state.update { it.copy(message = "Removed \"$domain\".") }
            } catch (e: Throwable) {
                _state.update { it.copy(error = e.message ?: "Failed to delete workspace.") }
            }
        }
    }

    fun toggleEnabled(domain: String, enabled: Boolean) {
        screenModelScope.launch {
            try {
                workspaceRepository.setEnabled(domain, enabled)
            } catch (e: Throwable) {
                _state.update { it.copy(error = e.message ?: "Failed to update workspace.") }
            }
        }
    }

    fun sendTest(domain: String, to: String, fromEmail: String?) {
        if (_state.value.isTestSending) return
        screenModelScope.launch {
            _state.update { it.copy(isTestSending = true, error = null, message = null) }
            val result = emailRepository.sendTestEmail(domain, to, fromEmail)
            _state.update {
                if (result.isSuccess) {
                    it.copy(isTestSending = false, message = "Test email sent to $to.")
                } else {
                    it.copy(
                        isTestSending = false,
                        error = result.exceptionOrNull()?.message ?: "Test send failed."
                    )
                }
            }
        }
    }

    fun openEmailTemplate(location: Location) {
        _state.update { it.copy(editingEmailTemplate = location) }
    }

    fun dismissEmailTemplate() {
        _state.update { it.copy(editingEmailTemplate = null) }
    }

    fun saveEmailTemplate(bodyTemplate: String) {
        val location = _state.value.editingEmailTemplate ?: return
        screenModelScope.launch {
            _state.update { it.copy(isSavingTemplate = true, error = null) }
            try {
                val updated = location.copy(
                    emailBodyTemplate = bodyTemplate.trim().ifBlank { null },
                    emailSignatureLine = null
                )
                locationRepository.updateLocation(location.id, updated)
                _state.update { it.copy(isSavingTemplate = false, editingEmailTemplate = null, message = "Email template saved.") }
            } catch (e: Throwable) {
                _state.update { it.copy(isSavingTemplate = false, error = e.message ?: "Failed to save template.") }
            }
        }
    }

    fun clearMessages() {
        _state.update { it.copy(message = null, error = null) }
    }
}
